import React, { useMemo } from 'react';
import {
  useAppDispatch,
  useAppSelector,
  useTranslate,
  useWidgetParam,
  useWidgetsTheme as theme,
} from '@src/core/hooks';
import {
  productAdderStepState,
  isCurrentStepValid,
  currentProductAdderStatus,
  productSelector,
  goToNextStep,
} from '@src/stores/product';
import { useWidgetId } from '@src/core/IdProvider';
import { GradientButton } from '@src/components/reusable';
import { track } from '@src/services/analytics';
import useSelectedProductId from '@src/hooks/useSelectedProductId';
import { useIsSingleBookingPackageBooking } from '@src/features/productPackage';

type ProductAdderNextStepButtonProps = {
  disabled?: boolean;
};

const ProductAdderNextStepButton = (props: ProductAdderNextStepButtonProps) => {
  const { disabled } = props;
  const isSingleBooking = useIsSingleBookingPackageBooking();
  const widgetId = useWidgetId();
  const status = useAppSelector(currentProductAdderStatus(widgetId));
  const cartAddCtaText = useWidgetParam('cart-add-cta-text');
  const stepIsValid = useAppSelector(isCurrentStepValid(widgetId));
  const { isAtLast } = useAppSelector(productAdderStepState(widgetId));
  const dispatch = useAppDispatch();
  const actionColor = theme('actionColor');
  const { i18n } = useTranslate();
  const productId = useSelectedProductId();
  const product = useAppSelector(productSelector(productId))!;

  const handleGoForwards = () => {
    if (!isAtLast) {
      track('productAdderNextStep', {
        productId: productId,
        productName: product.name,
      });
    }

    dispatch(goToNextStep(widgetId));
  };

  const getCtaText = () => (cartAddCtaText ? cartAddCtaText : i18n('adder_purchaseButton'));

  const disableButton = useMemo(
    () => !stepIsValid || status === 'adding_to_cart' || disabled,
    [stepIsValid, status, disabled],
  );

  return (
    <GradientButton
      disabled={disableButton}
      actionColor={actionColor}
      onClick={handleGoForwards}
      dataCy='productAdderNextStepButton'
    >
      {isAtLast && !isSingleBooking ? getCtaText() : i18n('adder_goForwardsFooter')}
    </GradientButton>
  );
};

export default ProductAdderNextStepButton;
